import React from "react";

/**
 * Grid of result cards — the primary output of Studios that generate N items
 * in parallel (like Game Asset Creator). Each card has three possible states:
 *
 *   - processing: a loading tile
 *   - success: shows the generated media with a "use this" action
 *   - failed: shows the error + a retry button
 *
 * Expected item shape (map-node iteration contract):
 *   {
 *     status: 'processing' | 'success' | 'failed',
 *     iteration_index: number,
 *     output_items?: CanonicalItem[],   // present on success
 *     error?: string,                   // present on failure
 *   }
 *
 * `onRetry(iteration_index)` fires the per-card retry.
 */
export default function StudioResultGrid({ items, onRetry }) {
  if (!items || items.length === 0) {
    return <div className="studio-result-grid-empty">No results yet.</div>;
  }

  return (
    <div className="studio-result-grid">
      {items.map((item, idx) => {
        const key = item?.iteration_index ?? idx;

        if (!item || item.status === "processing") {
          return (
            <div
              key={key}
              className="studio-result-card studio-result-card-processing"
            >
              <div className="studio-result-card-spinner" />
              <div className="studio-result-card-label">Generating...</div>
            </div>
          );
        }

        if (item.status === "failed") {
          return (
            <div
              key={key}
              className="studio-result-card studio-result-card-failed"
            >
              <div className="studio-result-card-error-icon">⚠</div>
              <div className="studio-result-card-label">
                {item.error || "Generation failed"}
              </div>
              {onRetry && (
                <button
                  className="studio-result-card-retry-btn"
                  onClick={() => onRetry(item.iteration_index ?? idx)}
                >
                  Retry
                </button>
              )}
            </div>
          );
        }

        // Success — read the first output_items entry (canonical shape)
        const firstItem =
          Array.isArray(item.output_items) && item.output_items.length > 0
            ? item.output_items[0]
            : null;
        const imageUrl = firstItem?.content ?? firstItem?.external_url ?? null;

        return (
          <div
            key={key}
            className="studio-result-card studio-result-card-success"
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={`Generated asset ${key}`}
                className="studio-result-card-image"
              />
            ) : (
              <div className="studio-result-card-placeholder">
                Result ready
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
